import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { FiUploadCloud, FiX, FiFile, FiImage, FiFileText } from 'react-icons/fi';

const ACCEPT_DEFAULT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg';
const MAX_SIZE_DEFAULT = 10 * 1024 * 1024; // 10MB

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const fileIcon = (file) => {
  if (file.type.startsWith('image/')) return <FiImage className="tw-text-blue-400" size={16} />;
  if (file.type.includes('pdf'))      return <FiFileText className="tw-text-red-400" size={16} />;
  return <FiFile className="tw-text-gray-400" size={16} />;
};

const FileAttachment = ({
  files = [],
  onChange,
  accept = ACCEPT_DEFAULT,
  maxSize = MAX_SIZE_DEFAULT,
  maxFiles = 10,
  disabled = false,
  label = 'Attachments',
}) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = React.useState(false);
  const [errors, setErrors] = React.useState([]);

  const processFiles = (incoming) => {
    const newErrors = [];
    const valid = [];

    Array.from(incoming).forEach((file) => {
      if (file.size > maxSize) {
        newErrors.push(`"${file.name}" exceeds ${formatBytes(maxSize)} limit`);
        return;
      }
      const isDuplicate = files.some((f) => f.name === file.name && f.size === file.size);
      if (isDuplicate) return;
      valid.push(file);
    });

    const merged = [...files, ...valid];
    if (merged.length > maxFiles) {
      newErrors.push(`Maximum ${maxFiles} files allowed`);
      valid.splice(maxFiles - files.length);
    }

    setErrors(newErrors);
    if (valid.length > 0) onChange([...files, ...valid]);
  };

  const remove = (index) => {
    const updated = files.filter((_, i) => i !== index);
    onChange(updated);
    setErrors([]);
  };

  const onDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!disabled) processFiles(e.dataTransfer.files);
  };

  const onInputChange = (e) => {
    if (e.target.files) processFiles(e.target.files);
    e.target.value = '';
  };

  return (
    <div>
      {label && (
        <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500 tw-mb-2">
          {label}
        </p>
      )}

      {/* Drop zone */}
      <div
        onDragEnter={onDrag}
        onDragLeave={onDrag}
        onDragOver={onDrag}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={[
          'tw-border-2 tw-border-dashed tw-rounded tw-px-4 tw-py-5 tw-flex tw-flex-col tw-items-center tw-gap-1',
          'tw-transition-colors tw-cursor-pointer tw-select-none',
          dragActive ? 'tw-border-blue-400 tw-bg-blue-50' : 'tw-border-gray-200 tw-bg-gray-50 hover:tw-border-blue-300 hover:tw-bg-blue-50',
          disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : '',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          onChange={onInputChange}
          disabled={disabled}
          className="tw-hidden"
        />
        <FiUploadCloud
          size={24}
          className={dragActive ? 'tw-text-blue-500' : 'tw-text-gray-400'}
        />
        <p className="tw-text-xs tw-text-gray-500 tw-m-0">
          <span className="tw-font-medium tw-text-blue-500">Click to upload</span> or drag &amp; drop
        </p>
        <p className="tw-text-xs tw-text-gray-400 tw-m-0">
          {accept.replace(/\./g, '').toUpperCase().split(',').join(' · ')} — max {formatBytes(maxSize)}
        </p>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="tw-mt-2 tw-space-y-1">
          {errors.map((err, i) => (
            <p key={i} className="tw-text-xs tw-text-red-500 tw-m-0">{err}</p>
          ))}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <ul className="tw-mt-3 tw-space-y-1 tw-m-0 tw-p-0 tw-list-none">
          {files.map((file, idx) => (
            <li
              key={idx}
              className="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-gray-100 tw-rounded-md tw-px-3 tw-py-2 tw-group"
            >
              {fileIcon(file)}
              <span className="tw-flex-1 tw-text-xs tw-text-gray-700 tw-truncate">{file.name}</span>
              <span className="tw-text-xs tw-text-gray-400 tw-shrink-0">{formatBytes(file.size)}</span>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="tw-text-gray-300 hover:tw-text-red-500 tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer tw-transition-colors"
              >
                <FiX size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

FileAttachment.propTypes = {
  files:    PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  onChange: PropTypes.func.isRequired,
  accept:   PropTypes.string,
  maxSize:  PropTypes.number,
  maxFiles: PropTypes.number,
  disabled: PropTypes.bool,
  label:    PropTypes.string,
};

export default FileAttachment;
